import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError, Subject } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@selfai-platform/shared';
import { ChatThread, ChatMessage, AgentType } from '../models';
import { LLMOrchestratorService } from './llm-orchestrator.service';
import { ThreadMessage, UserThread, GetAllThreadsResponse } from '../types/service-interfaces';
import { ChatThreadFactory, ChatMessageFactory } from '../factories';

@Injectable({
  providedIn: 'root',
})
export class ThreadManagementService {
  private readonly destroy$ = new Subject<void>();
  private readonly threadsSubject = new BehaviorSubject<ChatThread[]>([]);
  private readonly activeThreadSubject = new BehaviorSubject<ChatThread | null>(null);
  private readonly isLoadingSubject = new BehaviorSubject<boolean>(false);

  public threads$ = this.threadsSubject.asObservable();
  public activeThread$ = this.activeThreadSubject.asObservable();

  private get currentUserId(): string {
    return this.llmOrchestratorService.getCurrentUserId();
  }

  constructor(
    private llmOrchestratorService: LLMOrchestratorService,
    private translateService: TranslateService,
    private readonly alertService: AlertService
  ) {}

  public loadAllUserThreads(): Observable<ChatThread[]> {
    this.isLoadingSubject.next(true);

    return this.llmOrchestratorService.getAllUserThreads().pipe(
      map((userThreads) => {
        const threads: ChatThread[] = [];

        if (userThreads && Array.isArray(userThreads)) {
          userThreads.forEach((userThread) => {
            const thread = ChatThreadFactory.fromUserThread(
              {
                thread_id: userThread.thread_id,
                title: userThread.title || this.translateService.instant('chat.threads.untitled-thread'),
                created_at: userThread.created_at,
                updated_at: userThread.updated_at,
                message_count: userThread.message_count
              },
              'chat_agent' as AgentType,
              []
            );
            threads.push(thread);
          });
        }

        threads.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());

        this.threadsSubject.next(threads);
        this.isLoadingSubject.next(false);

        return threads;
      }),
      catchError((error) => {
        this.alertService.error(this.translateService.instant('chat.errors.failed-to-load-user-threads') + ': ' + (error.message || error));
        this.isLoadingSubject.next(false);
        return of([]);
      })
    );
  }

  public loadThreadsForAgent(agentType: AgentType): Observable<ChatThread[]> {
    return this.loadAllUserThreads().pipe(
      map(threads => threads.filter(thread => thread.agentType === agentType))
    );
  }

  public createEmptyThread(agentType: AgentType): ChatThread {
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const emptyThread = ChatThreadFactory.createThread({
      title: this.translateService.instant('chat.threads.new-thread'),
      agentType,
      isActive: true,
      messages: [],
      metadata: { messageCount: 0 }
    });

    const tempThread = { ...emptyThread, id: tempId };

    const currentThreads = this.threadsSubject.value;
    const updatedThreads = [tempThread, ...currentThreads.map(t => ({ ...t, isActive: false }))];
    this.threadsSubject.next(updatedThreads);
    this.activeThreadSubject.next(tempThread);

    return tempThread;
  }

  public createActualThread(tempThreadId: string, agentType: AgentType, firstMessage: string): Observable<ChatThread> {
    this.isLoadingSubject.next(true);

    return this.llmOrchestratorService.createThread(agentType).pipe(
      map((response) => {
        const currentThreads = this.threadsSubject.value;
        const tempThread = currentThreads.find(t => t.id === tempThreadId);
        const existingMessages = tempThread?.messages || [];

        const newThread = ChatThreadFactory.fromUserThread(
          {
            thread_id: response.thread_id,
            title: this.translateService.instant('chat.threads.new-thread'),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            message_count: existingMessages.length
          },
          agentType,
          existingMessages
        );

        const updatedThreads = currentThreads.map(thread =>
          thread.id === tempThreadId ? { ...newThread, isActive: true } : thread
        );

        this.threadsSubject.next(updatedThreads);
        this.setActiveThread({ ...newThread, isActive: true });
        this.isLoadingSubject.next(false);

        return newThread;
      }),
      catchError((error) => {
        this.alertService.error(this.translateService.instant('chat.errors.failed-to-create-thread-api') + ': ' + (error.message || error));
        this.isLoadingSubject.next(false);
        return throwError(() => error);
      })
    );
  }

  public createThread(title: string, agentType: AgentType): Observable<ChatThread> {
    return this.llmOrchestratorService.createThread(agentType).pipe(
      map((response) => {
        const newThread = ChatThreadFactory.fromUserThread(
          {
            thread_id: response.thread_id,
            title: title || this.translateService.instant('chat.threads.default-title', { agentType }),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            message_count: 0
          },
          agentType,
          []
        );

        const currentThreads = this.threadsSubject.value;
        const updatedThreads = [...currentThreads, newThread];
        this.threadsSubject.next(updatedThreads);
        this.setActiveThread(newThread);

        return newThread;
      }),
      catchError((error) => {
        this.alertService.error(this.translateService.instant('chat.errors.failed-to-create-thread-api') + ': ' + (error.message || error));
        return throwError(() => error);
      })
    );
  }

  public deleteThread(threadId: string): Observable<void> {
    const currentThreads = this.threadsSubject.value;
    const threadToDelete = currentThreads.find(t => t.id === threadId);

    if (!threadToDelete) {
      return throwError(() => new Error(this.translateService.instant('chat.errors.thread-not-found')));
    }

    this.isLoadingSubject.next(true);

    return this.llmOrchestratorService.deleteThreadHistory(threadId).pipe(
      tap(() => {
        const updatedThreads = currentThreads.filter(t => t.id !== threadId);
        this.threadsSubject.next(updatedThreads);

        if (this.activeThreadSubject.value?.id === threadId) {
          const newActiveThread = updatedThreads.length > 0 ? updatedThreads[0] : null;
          this.activeThreadSubject.next(newActiveThread);
        }

        this.isLoadingSubject.next(false);
      }),
      catchError((error) => {
        this.alertService.error(this.translateService.instant('chat.errors.failed-to-delete-thread-api') + ': ' + (error.message || error));
        this.isLoadingSubject.next(false);
        return throwError(() => error);
      })
    );
  }

  public setActiveThread(thread: ChatThread | null): void {
    const currentThreads = this.threadsSubject.value;
    const updatedThreads = currentThreads.map(t => ({
      ...t,
      isActive: t.id === thread?.id
    }));

    this.threadsSubject.next(updatedThreads);
    this.activeThreadSubject.next(thread);
  }

  public getActiveThread(): ChatThread | null {
    return this.activeThreadSubject.value;
  }

  public addMessageToThread(threadId: string, message: ChatMessage): Observable<void> {
    const currentThreads = this.threadsSubject.value;
    const updatedThreads = currentThreads.map(thread => {
      if (thread.id === threadId) {
        return {
          ...thread,
          messages: [...thread.messages, message],
          updatedAt: new Date(),
        };
      }
      return thread;
    });

    this.threadsSubject.next(updatedThreads);

    const activeThread = this.activeThreadSubject.value;
    if (activeThread && activeThread.id === threadId) {
      const updatedActiveThread = updatedThreads.find(t => t.id === threadId);
      if (updatedActiveThread) {
        this.activeThreadSubject.next(updatedActiveThread);
      }
    }

    return of(void 0);
  }

  public updateThreadTitle(threadId: string, newTitle: string): Observable<void> {
    const currentThreads = this.threadsSubject.value;
    const updatedThreads = currentThreads.map(thread => {
      if (thread.id === threadId) {
        return {
          ...thread,
          title: newTitle,
          updatedAt: new Date(),
        };
      }
      return thread;
    });

    this.threadsSubject.next(updatedThreads);

    if (this.activeThreadSubject.value?.id === threadId) {
      const updatedActiveThread = updatedThreads.find(t => t.id === threadId);
      if (updatedActiveThread) {
        this.activeThreadSubject.next(updatedActiveThread);
      }
    }

    return of(void 0);
  }

  public loadThreadHistory(threadId: string): Observable<ChatMessage[]> {
    const thread = this.threadsSubject.value.find(t => t.id === threadId);
    if (!thread) {
      return throwError(() => new Error(this.translateService.instant('chat.errors.thread-not-found')));
    }

    this.isLoadingSubject.next(true);

    return this.llmOrchestratorService.getThreadHistory(threadId).pipe(
      map((response) => {
        const messages = this.convertThreadMessages(response.messages);

        const currentThreads = this.threadsSubject.value;
        const updatedThreads = currentThreads.map(t =>
          t.id === threadId ? {
            ...t,
            messages,
            metadata: {
              ...t.metadata,
              app_id: response.metadata?.app_id
            }
          } : t
        );
        this.threadsSubject.next(updatedThreads);

        if (this.activeThreadSubject.value?.id === threadId) {
          const updatedActiveThread = updatedThreads.find(t => t.id === threadId);
          if (updatedActiveThread) {
            this.activeThreadSubject.next(updatedActiveThread);
          }
        }

        this.isLoadingSubject.next(false);
        return messages;
      }),
      catchError((error) => {
        this.alertService.error(this.translateService.instant('chat.errors.failed-to-load-thread-history') + ': ' + (error.message || error));
        this.isLoadingSubject.next(false);
        return throwError(() => error);
      })
    );
  }

  private generateThreadTitle(messages: ThreadMessage[]): string {
    if (messages.length === 0) return this.translateService.instant('chat.threads.new-thread');

    const firstUserMessage = messages.find(m => m.role === 'user');
    if (firstUserMessage) {
      const content = firstUserMessage.content.slice(0, 50);
      return content.length < firstUserMessage.content.length ? `${content}...` : content;
    }

    return this.translateService.instant('chat.threads.new-thread');
  }

  private convertThreadMessages(threadMessages: ThreadMessage[]): ChatMessage[] {
    return threadMessages.map((msg) =>
      ChatMessageFactory.fromThreadMessage(msg)
    );
  }

  public createLocalThread(threadId: string, agentType: AgentType): Observable<ChatThread> {
    const newThread = ChatThreadFactory.createLocalThread(threadId, agentType);

    const currentThreads = this.threadsSubject.value;
    const updatedThreads = [...currentThreads, newThread];
    this.threadsSubject.next(updatedThreads);

    this.setActiveThread(newThread);

    return of(newThread);
  }

  public isTemporaryThread(threadId: string): boolean {
    return threadId.startsWith('temp-');
  }

  public updateThreadWithGeneratedTitle(threadId: string, newTitle: string): void {
    const currentThreads = this.threadsSubject.value;
    const updatedThreads = currentThreads.map(thread => {
      if (thread.id === threadId) {
        return {
          ...thread,
          title: newTitle,
          updatedAt: new Date(),
        };
      }
      return thread;
    });

    this.threadsSubject.next(updatedThreads);

    if (this.activeThreadSubject.value?.id === threadId) {
      const updatedActiveThread = updatedThreads.find(t => t.id === threadId);
      if (updatedActiveThread) {
        this.activeThreadSubject.next(updatedActiveThread);
      }
    }
  }
}
